import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import wordmark from "@/assets/pnrbet-wordmark.png";

/* Admin navigation — grouped and styled to mirror the public (user-facing) Shell
   sidebar 1:1: same button size (h-34 / gap-3 / text-sm / icon 18), the app's own
   Icon set (so shared items like Support match), titled sections, and -fill active
   glyphs in PRYMEBET green. Route-backed surfaces may provide `href`; legacy Admin.jsx
   sections continue using their unchanged `key` values. */
export const ADMIN_NAV_GROUPS = [
  { title: "Sportsbook", items: [
    { key: "markets", label: "Market Ops", icon: "sportsbook", href: "/admin/markets" },
    { key: "events", label: "Events", icon: "sportsbook" },
    { key: "props", label: "Grade Props", icon: "grade", badgeKey: "props", bb: "#27272A" },
    { key: "engine", label: "Engine / Odds", icon: "sportsbook" },
    { key: "proam", label: "Pro-Am Basketball", icon: "sportsbook", superAdminOnly: true },
  ] },
  { title: "Casino", items: [
    { key: "dealer", label: "Live Dealer", icon: "livedealer" },
    { key: "casinolog", label: "Casino Log", icon: "dice" },
  ] },
  { title: "Finance", items: [
    { key: "house", label: "House P/L", icon: "pnl" },
    { key: "cash", label: "Deposits / Withdrawals", icon: "transfer", badgeKey: "cash", bb: "#3F3F46" },
    { key: "payouts", label: "Payouts", icon: "transfer", badgeKey: "payouts", bb: "#3F3F46", superAdminOnly: true },
  ] },
  { title: "Players", items: [
    { key: "users", label: "Users", icon: "account" },
    { key: "kyc", label: "KYC", icon: "verified", badgeKey: "kyc", bb: "#3F3F46" },
    { key: "allbets", label: "All Bets", icon: "mybets" },
  ] },
  { title: "Platform", items: [
    { key: "audit", label: "Audit Log", icon: "audit" },
    { key: "support", label: "Support", icon: "support", badgeKey: "support", bb: "#EF4444" },
    { key: "owner", label: "Platform", icon: "platform", ownerOnly: true },
  ] },
];

// Flat list kept for compatibility (Admin.jsx looks up the active item's label for the mobile title).
export const ADMIN_NAV = ADMIN_NAV_GROUPS.flatMap((g) => g.items);

function NavItem({ item, active, badge, onClick, mobile }) {
  const on = active === item.key;
  const h = mobile ? "h-11" : "h-[34px]";
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-[10px] px-2.5 text-sm ${h} ${
        on
          ? "bg-pnr-green/[0.07] font-semibold text-white shadow-[inset_0_0_0_1px_rgba(0,214,50,0.22)]"
          : "font-medium text-muted-foreground hover:bg-[#00B829]/5 hover:text-white"
      }`}
    >
      <Icon name={on ? `${item.icon}-fill` : item.icon} size={18} className={on ? "text-pnr-green" : ""} />
      <span className="flex-1 truncate text-left">{item.label}</span>
      {badge != null && (
        <span className="rounded-full px-1.5 text-[10px] font-extrabold leading-[17px] text-white" style={{ background: item.bb }}>{badge}</span>
      )}
    </button>
  );
}

function NavGroups({ groups, active, badges, onPick, mobile }) {
  return (
    <>
      {groups.map((grp) => (
        <div key={grp.title} className="flex flex-col gap-0.5">
          <div className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#52525b]">{grp.title}</div>
          {grp.items.map((it) => (
            <NavItem
              key={it.key}
              item={it}
              mobile={mobile}
              active={active}
              badge={it.badgeKey ? badges?.[it.badgeKey] || null : null}
              onClick={() => onPick(it)}
            />
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * AdminShell — the dedicated admin surface, styled to mirror the user-facing Shell.
 * Desktop: fixed 248px sidebar. Mobile: top bar + slide-over drawer. 1:1 with the public sidebar.
 */
export default function AdminShell({ active, onChange, badges, user, title, children }) {
  const [drawer, setDrawer] = useState(false);
  const navigate = useNavigate();
  const isOwner = user?.role === "owner" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const groups = ADMIN_NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((n) => (!n.ownerOnly || isOwner) && (!n.superAdminOnly || isSuperAdmin)),
    }))
    .filter((g) => g.items.length);
  const initials = (user?.username || "AD").slice(0, 2).toUpperCase();
  const pick = (item) => {
    if (item?.href) navigate(item.href);
    else onChange?.(item?.key);
    setDrawer(false);
  };

  const BackToApp = (
    <button
      onClick={() => navigate("/")}
      className="flex h-9 shrink-0 items-center gap-2 rounded-[10px] border border-surface-border bg-surface-4 px-3 text-[13px] font-semibold text-muted-foreground hover:text-white"
    >
      <Icon name="arrow-left" size={16} />
      Back To App
    </button>
  );

  const Header = (
    <div className="flex items-center gap-2 px-1.5 pt-1">
      <img src={wordmark} alt="PRYMEBET" className="h-7 w-auto" />
      <span className="rounded-[5px] bg-surface-5 px-1.5 py-0.5 text-[9px] font-extrabold tracking-[.12em] text-muted-foreground">ADMIN</span>
    </div>
  );

  const AdminCard = (
    <div className="mt-auto flex items-center gap-2.5 rounded-2xl border border-white/[0.06] bg-surface-4 p-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E4E4E7] font-display text-[11px] font-extrabold text-surface-1">{initials}</span>
      <div className="min-w-0">
        <div className="truncate text-[12px] font-bold text-white">{user?.username || "admin"}</div>
        <div className="text-[10px] font-medium capitalize text-muted-foreground">{(user?.role || "admin").replace("_", " ")}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-1 text-white">
      {/* Desktop sidebar — mirrors the public Shell sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] flex-col gap-3 overflow-y-auto border-r border-surface-border bg-surface-2 px-3.5 pb-3.5 pt-4 text-white lg:flex">
        {Header}
        {BackToApp}
        <NavGroups groups={groups} active={active} badges={badges} onPick={pick} />
        {AdminCard}
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-20 border-b border-surface-border bg-surface-2 lg:hidden">
        <div className="flex h-14 items-center gap-1.5 px-2.5">
          <button onClick={() => setDrawer(true)} className="flex h-11 w-11 items-center justify-center rounded-xl" aria-label="Menu">
            <Icon name="menu" size={22} className="text-white" />
          </button>
          <span className="font-display text-[18px] font-extrabold tracking-tight">{title || "Admin"}</span>
          <span className="rounded-[5px] bg-surface-5 px-1.5 py-0.5 text-[9px] font-extrabold tracking-[.12em] text-muted-foreground">ADMIN</span>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 flex w-[290px] flex-col gap-2.5 overflow-y-auto border-r border-surface-border bg-surface-2 px-3.5 pb-6 pt-4">
            <div className="flex items-center gap-2 px-1.5">
              <img src={wordmark} alt="PRYMEBET" className="h-7 w-auto" />
              <span className="rounded-[5px] bg-surface-5 px-1.5 py-0.5 text-[9px] font-extrabold tracking-[.12em] text-muted-foreground">ADMIN</span>
              <button onClick={() => setDrawer(false)} className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground"><Icon name="x" size={18} /></button>
            </div>
            {BackToApp}
            <NavGroups groups={groups} active={active} badges={badges} onPick={pick} mobile />
            {AdminCard}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-[248px]">
        <div className="flex flex-col gap-4 px-4 py-5 sm:px-6 lg:px-[26px] lg:py-[22px]">{children}</div>
      </main>
    </div>
  );
}
